import React, { Component, useEffect, useRef, useState } from 'react'
import { Link, Redirect } from 'react-router-dom'
import axios from 'axios'
import Modal from 'react-bootstrap/Modal'
import Cookies from 'universal-cookie'
import { v4 as uuidv4 } from 'uuid'
import { BiSolidLocationPlus } from 'react-icons/bi'
import { BsArrowRight, BsClock, BsSearch } from 'react-icons/bs'
import Header from './layouts/Header'
import IndexedEvents from './IndexedEvents'
import { API_URL } from './config'
import './App.css'
import './Theme.css'
import './FeesPage.css' // the Feepage stylesheet (same import as Budget.jsx)
import './Home.css' // home-only additions (replaces the old Home.css)

const cookies = new Cookies()
if (!cookies.get('clientid')) {
  cookies.set('clientid', uuidv4().toUpperCase())
}

const BsTicketPerforated = (props) => (
  <svg
    viewBox="0 0 24 24"
    width="1em"
    height="1em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinejoin="round"
    strokeLinecap="round"
    {...props}
  >
    <path d="M4 6h16a1 1 0 0 1 1 1v3a2 2 0 0 0 0 4v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-3a2 2 0 0 0 0-4V7a1 1 0 0 1 1-1z" />
    <path d="M14 8v1.5M14 11.25v1.5M14 14.5V16" />
  </svg>
)

const WS = `${API_URL}/apps/kscanner/webservices.php`
const pad = (n) => String(n).padStart(2, '0')

const todayISO = () => {
  const d = new Date()
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const posterUrl = (item) =>
  `${API_URL}/apps/kscanner/posters/${item.businessid}/${item.eventid}/${item.poster}`

// Descriptions come back as HTML; card previews only need plain text.
const plainText = (html = '', max = 160) => {
  const text = new DOMParser().parseFromString(String(html), 'text/html').body.textContent || ''
  const clean = text.replace(/\s+/g, ' ').trim()
  return clean.length > max ? clean.slice(0, max).trimEnd() + '…' : clean
}

const hidePoster = (e) => {
  e.currentTarget.style.display = 'none'
}

// Cursor-follow spotlight on cards.
const spot = (e) => {
  const r = e.currentTarget.getBoundingClientRect()
  e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`)
  e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`)
}

// Fade/slide in when scrolled into view.
const Reveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null)
  const [seen, setSeen] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      setSeen(true)
      return undefined
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true)
          io.disconnect()
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={`fp-reveal ${seen ? 'is-in' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  )
}

const WORDS = ['concert', 'festival', 'night out', 'experience', 'weekend']

const RotatingWord = () => {
  const [i, setI] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % WORDS.length), 2600)
    return () => clearInterval(t)
  }, [])
  return (
    <span className="fp-rot" aria-live="off">
      <span key={WORDS[i]} className="fp-rot__word">
        {WORDS[i]}
      </span>
    </span>
  )
}

const STEPS = [
  ['1', 'Pick an event', 'Search by name, venue or category, then open the event you want.'],
  ['2', 'Buy your ticket', 'Choose your ticket type and pay online.'],
  ['3', 'Walk in', 'Show your ticket at the door and enjoy the event.'],
]

class Home extends Component {
  constructor(props) {
    super(props)
    this.state = {
      events: [],
      loading: true,
      error: null,
      provinces: [],
      categories: [],
      query: '',
      category: '',
      email: '',
      phone: '',
      customername: '',
      affiliate: '',
      affiliate_email: '',
      affiliate_phone: '',
      resumeModal: false,
      detailModal: false,
    }
    this.eventsRef = React.createRef()
  }

  componentDidMount() {
    document.title = "Karamale - World's digital ticket software"
    this.loadAffiliate()
    this.loadCustomer()
    this.loadEvents()
    this.loadFilters()
  }

  loadAffiliate() {
    const fromUrl = new URLSearchParams(window.location.search).get('affiliateid')
    const stored = localStorage.getItem('affiliateid')
    if (fromUrl && (!stored || stored === 'null')) {
      localStorage.setItem('affiliateid', fromUrl)
    }
    const affiliateid = localStorage.getItem('affiliateid')
    if (!affiliateid || affiliateid === 'null') return

    axios
      .get(WS, { params: { query: 'AFFILIATEDETAILS', affiliateid } })
      .then((res) => {
        if (!res.data || !res.data.affiliate) return
        this.setState({
          affiliate: res.data.affiliate,
          affiliate_email: res.data.email,
          affiliate_phone: res.data.phone,
          resumeModal: true,
        })
      })
      .catch(() => {})
  }

  loadCustomer() {
    const customerid = localStorage.getItem('customerid')
    if (customerid === null) return
    axios
      .get(WS, {
        params: { query: 'CUSTOMERDETAILS', accountid: customerid },
        withCredentials: true,
      })
      .then((res) => {
        this.setState({
          email: res.data.email,
          phone: res.data.phone,
          customername: res.data.customername,
        })
      })
      .catch(() => {})
  }

  loadEvents() {
    this.setState({ loading: true, error: null })
    axios
      .get(WS, { params: { query: 'EVENTS' } })
      .then((res) => {
        this.setState({ events: Array.isArray(res.data) ? res.data : [], loading: false })
      })
      .catch((error) => this.setState({ error, loading: false }))
  }

  loadFilters() {
    axios
      .get(WS, { params: { query: 'PROVINCES' } })
      .then((res) => this.setState({ provinces: Array.isArray(res.data) ? res.data : [] }))
      .catch(() => {})
    axios
      .get(WS, { params: { query: 'EVENTCATEGORY', limit: 9 } })
      .then((res) => this.setState({ categories: Array.isArray(res.data) ? res.data : [] }))
      .catch(() => {})
  }

  cancel = () => this.setState({ resumeModal: false, detailModal: false })
  details = () => this.setState({ resumeModal: false, detailModal: true })
  back = () => this.setState({ resumeModal: true, detailModal: false })
  signUp = () => this.props.history.push({ pathname: '/organiser-registration' })
  learnMore = () => this.props.history.push({ pathname: '/aboutkaramale' })

  onSearch = (e) => {
    e.preventDefault()
    if (this.eventsRef.current) {
      this.eventsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  onProvince = (e) => {
    const provinceid = e.target.value
    if (!provinceid) return
    const p = this.state.provinces.find((x) => String(x.provinceid) === provinceid)
    this.props.history.push('/explore', {
      fromHome: true,
      provinceid,
      category: '',
      province: p ? p.province : '',
    })
  }

  clearFilters = () => this.setState({ query: '', category: '' })

  eventLink = (item, session) => ({
    pathname: `/event/${item.eventid}/tickets`,
    state: {
      fromHome: true,
      eventid: item.eventid,
      eventDate: item.event_day,
      fulldate: session.today,
      customerid: session.customerid,
      posterUri: posterUrl(item),
      accountid: session.accountid,
      accounttype: session.accounttype,
      customername: this.state.customername,
      email: this.state.email,
      phone: this.state.phone,
      venue: item.event_location,
      description: item.event_des,
      businessid: item.businessid,
      poster: item.poster,
      eventname: item.eventname,
      eventday: item.eventday,
      starttime: item.start_time,
      endtime: item.finish_time,
      previousPage: '',
      campaignStatus: 'running',
      category: '',
    },
  })

  renderTicket(item, session, i) {
    return (
      <Reveal key={item.eventid || i} delay={(i % 3) * 90} className="fp-cell">
        <Link
          to={this.eventLink(item, session)}
          className="fp-event"
          aria-label={`${item.event_name}, ${item.eventday}`}
          onMouseMove={spot}
        >
          <div className="fp-event__bg">
            <BsTicketPerforated aria-hidden="true" />
            <img src={posterUrl(item)} alt="" loading="lazy" onError={hidePoster} />
          </div>
          <div className="fp-event__shade" aria-hidden="true" />

          {item.eventcategory && <span className="fp-event__cat">{item.eventcategory}</span>}

          <div className="fp-event__info">
            <h3 className="fp-event__title">{item.event_name}</h3>
            <p className="fp-event__place">
              <BiSolidLocationPlus aria-hidden="true" />
              <span>{item.event_location}</span>
            </p>
            <p className="fp-event__desc">{plainText(item.event_des, 90)}</p>
          </div>

          <div className="fp-event__stub">
            <div className="fp-event__when">
              <strong>{item.eventday}</strong>
              {item.start_time && (
                <span>
                  <BsClock aria-hidden="true" /> {item.start_time}
                </span>
              )}
            </div>
            <span className="fp-event__cta">
              Get tickets <BsArrowRight aria-hidden="true" />
            </span>
          </div>
        </Link>
      </Reveal>
    )
  }

  render() {
    const isCustomer = localStorage.getItem('customername') !== null

    if (!isCustomer) {
      if (
        localStorage.getItem('businessname') !== null &&
        localStorage.getItem('accounttype') !== null
      ) {
        return <Redirect to="/dashboard" />
      }
      if (localStorage.getItem('partnerid') !== null) {
        return <Redirect to="/in" />
      }
    }

    const {
      events,
      loading,
      error,
      provinces,
      categories,
      query,
      category,
      affiliate,
      affiliate_email,
      affiliate_phone,
    } = this.state

    const today = todayISO()
    const hasId = localStorage.getItem('customerid') !== null
    const session = {
      today,
      customerid: hasId ? localStorage.getItem('customerid') : 0,
      accountid: hasId ? localStorage.getItem('customerid') : 0,
      accounttype: hasId ? localStorage.getItem('accounttype') : '',
    }

    // Same rule as before: hide upcoming events whose balance is below 3.5.
    const visible = events.filter((e) => !(e.balance < 3.5 && e.event_day > today))
    const q = query.trim().toLowerCase()
    const shown = visible.filter(
      (e) =>
        (!category || e.eventcategory === category) &&
        (!q || `${e.event_name || ''} ${e.event_location || ''}`.toLowerCase().includes(q)),
    )
    const filtering = Boolean(q || category)
    const stack = !loading && !error ? visible.slice(0, 2) : []

    return (
      <div className="fp-page">
        <Header />

        {/* ---------- Hero (guests only) ---------- */}
        {!isCustomer && (
          <section className="fp-hero" aria-labelledby="fp-hero-title">
            <div className="fp-aurora" aria-hidden="true">
              <span className="fp-aurora__a" />
              <span className="fp-aurora__b" />
            </div>

            <div className="fp-wrap fp-hero__inner">
              <div>
                <span className="fp-pill">
                  <i />
                  Digital tickets · Instant entry
                </span>

                <h1 id="fp-hero-title">
                  Find your next <RotatingWord />
                </h1>

                <p className="fp-hero__sub">
                  Concerts, festivals and experiences near you. Pick an event, buy your ticket, walk
                  in.
                </p>

                <form className="fp-search" onSubmit={this.onSearch} role="search">
                  <label className="fp-search__field">
                    <BsSearch aria-hidden="true" />
                    <input
                      type="search"
                      placeholder="Search events or venues"
                      aria-label="Search events or venues"
                      value={query}
                      onChange={(e) => this.setState({ query: e.target.value })}
                    />
                  </label>

                  <label className="fp-search__field fp-search__field--select">
                    <BiSolidLocationPlus aria-hidden="true" />
                    <select aria-label="Browse by province" value="" onChange={this.onProvince}>
                      <option value="">All provinces</option>
                      {provinces.map((p) => (
                        <option key={p.provinceid} value={p.provinceid}>
                          {p.province}
                        </option>
                      ))}
                    </select>
                  </label>

                  <button type="submit" className="fp-btn fp-btn--brand">
                    Find events
                  </button>
                </form>

                <Link to="/explore" className="fp-hero__browse">
                  Browse all events <BsArrowRight aria-hidden="true" />
                </Link>
              </div>

              <div className="fp-hero__visual">
                {stack.length > 0 ? (
                  <div className="fp-hstack" aria-hidden="true">
                    {stack.map((item, i) => (
                      <Link
                        key={item.eventid || i}
                        to={this.eventLink(item, session)}
                        className={`fp-hmini ${i === 0 ? 'fp-hmini--a' : 'fp-hmini--b'}`}
                        tabIndex={-1}
                      >
                        <div
                          className="fp-hmini__img"
                          style={{ backgroundImage: `url(${posterUrl(item)})` }}
                        />
                        <div className="fp-hmini__body">
                          <p>{item.event_name}</p>
                          <span>{item.eventday}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="fp-bigcard fp-bigcard--ticket" aria-hidden="true">
                    <small>Instant entry</small>
                    <BsTicketPerforated />
                    <div className="fp-bigcard__line">Pick an event, buy your ticket, walk in.</div>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* ---------- Category marquee (guests only) ---------- */}
        {!isCustomer && categories.length > 0 && (
          <div className="fp-marquee" aria-hidden="true">
            <div className="fp-marquee__track">
              {[...categories, ...categories].map((c, i) => (
                <span className="fp-marquee__item" key={`${c.category}-${i}`}>
                  <i /> {c.category}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ---------- Events ---------- */}
        <section className="fp-section" ref={this.eventsRef} id="events" aria-live="polite">
          <div className="fp-wrap">
            <Reveal>
              <div className="fp-head">
                <h2 className="fp-h2">
                  Upcoming <span>events.</span>
                </h2>

                <div>
                  <p>Concerts, festivals and experiences happening around you.</p>
                  {!loading && !error && (
                    <span className="fp-count">
                      {shown.length} {shown.length === 1 ? 'event' : 'events'}
                    </span>
                  )}
                </div>
              </div>
            </Reveal>

            {isCustomer && (
              <div className="fp-search fp-search--inline">
                <label className="fp-search__field">
                  <BsSearch aria-hidden="true" />
                  <input
                    type="search"
                    placeholder="Search events or venues"
                    aria-label="Search events or venues"
                    value={query}
                    onChange={(e) => this.setState({ query: e.target.value })}
                  />
                </label>
              </div>
            )}

            {categories.length > 0 && (
              <div className="fp-chips" role="group" aria-label="Filter by category">
                <button
                  type="button"
                  className={`fp-chip ${!category ? 'is-active' : ''}`}
                  aria-pressed={!category}
                  onClick={() => this.setState({ category: '' })}
                >
                  All
                </button>

                {categories.map((c) => (
                  <button
                    key={c.category}
                    type="button"
                    className={`fp-chip ${category === c.category ? 'is-active' : ''}`}
                    aria-pressed={category === c.category}
                    onClick={() => this.setState({ category: c.category })}
                  >
                    {c.category}
                  </button>
                ))}

                <Link to="/categories" className="fp-chip fp-chip--dashed">
                  More categories
                </Link>
              </div>
            )}

            {loading && (
              <div className="fp-egrid" aria-busy="true" aria-label="Loading events">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div className="fp-skel" key={i}>
                    <div className="fp-skel__img" />
                    <div className="fp-skel__body">
                      <div className="fp-skel__line" />
                      <div className="fp-skel__line" />
                      <div className="fp-skel__line" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loading && error && (
              <div className="fp-state" role="alert">
                <BsTicketPerforated className="fp-state__icon" aria-hidden="true" />
                <h3>We couldn't load events</h3>
                <p>Check your connection and try again.</p>
                <div className="fp-actions fp-actions--center">
                  <button
                    type="button"
                    className="fp-btn fp-btn--brand"
                    onClick={() => this.loadEvents()}
                  >
                    Try again
                  </button>
                </div>
              </div>
            )}

            {!loading && !error && shown.length === 0 && (
              <div className="fp-state">
                <BsTicketPerforated className="fp-state__icon" aria-hidden="true" />
                <h3>{filtering ? 'No events match your search' : 'No upcoming events yet'}</h3>
                <p>
                  {filtering
                    ? 'Try a different name or category, or clear your filters.'
                    : 'New events are added often. Check back soon or browse everything on Explore.'}
                </p>
                <div className="fp-actions fp-actions--center">
                  {filtering && (
                    <button
                      type="button"
                      className="fp-btn fp-btn--brand"
                      onClick={this.clearFilters}
                    >
                      Clear filters
                    </button>
                  )}
                  <Link to="/explore" className="fp-btn fp-btn--ghost">
                    Browse all events
                  </Link>
                </div>
              </div>
            )}

            {!loading && !error && shown.length > 0 && (
              <div className="fp-egrid">
                {shown.map((item, i) => this.renderTicket(item, session, i))}
              </div>
            )}
          </div>
        </section>

        <section className="fp-section fp-section--tight">
          <div className="fp-wrap">
            <IndexedEvents />
          </div>
        </section>

        {/* ---------- How it works + organiser/affiliate (guests only) ---------- */}
        {!isCustomer && (
          <>
            <section className="fp-section" aria-labelledby="fp-how">
              <div className="fp-wrap">
                <div className="fp-flow">
                  <div>
                    <h2 id="fp-how" className="fp-h2">
                      How it <span>works.</span>
                    </h2>
                    <p className="fp-lead">Three steps from search to the door.</p>
                  </div>

                  <ol className="fp-steps">
                    {STEPS.map(([n, title, text]) => (
                      <li key={n}>
                        <span className="fp-steps__n">{n}</span>
                        <div>
                          <strong>{title}</strong>
                          <p>{text}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </section>

            <section className="fp-section fp-section--last">
              <div className="fp-wrap fp-two fp-cta-grid">
                <Reveal className="fp-cell">
                  <div className="fp-cta fp-cta--left">
                    <h2>
                      Sell tickets on <em>Karamale.</em>
                    </h2>
                    <p>
                      Create an organiser account, publish your event and get paid with transparent
                      payouts.
                    </p>
                    <div className="fp-actions">
                      <Link to="/organiser-registration" className="fp-btn fp-btn--light">
                        Get started
                      </Link>
                      <Link to="/about" className="fp-btn fp-btn--outline">
                        Learn more
                      </Link>
                    </div>
                  </div>
                </Reveal>

                <Reveal delay={120} className="fp-cell">
                  <div className="fp-card fp-card--hot fp-ctacard" onMouseMove={spot}>
                    <h2 className="fp-h2">
                      Earn as an <span>affiliate.</span>
                    </h2>
                    <p>
                      Refer organisers with your unique link. When they start selling tickets, you
                      earn up to 30%.
                    </p>
                    <div className="fp-actions">
                      <Link to="/affiliate-registration" className="fp-btn fp-btn--brand">
                        Get started
                      </Link>
                      <Link to="/aboutsalesparnter" className="fp-btn fp-btn--ghost">
                        Learn more
                      </Link>
                    </div>
                  </div>
                </Reveal>
              </div>
            </section>
          </>
        )}

        {/* ---------- Affiliate invite modals ---------- */}
        <Modal
          show={this.state.resumeModal}
          onHide={this.cancel}
          centered
          dialogClassName="fp-modal"
        >
          <div className="fp-modal__body">
            <span className="fp-modal__icon">
              <BsTicketPerforated aria-hidden="true" />
            </span>
            <h2 className="fp-modal__title">You're invited to Karamale</h2>
            <p className="fp-modal__text">
              {affiliate} invited you to create events and sell tickets with transparent payouts.
            </p>
            <div className="fp-modal__actions">
              <button type="button" className="fp-btn fp-btn--brand" onClick={this.signUp}>
                Sign up as an organiser
              </button>
              <button type="button" className="fp-btn fp-btn--ghost" onClick={this.learnMore}>
                Learn more
              </button>
            </div>
            <div className="fp-modal__foot">
              <button type="button" className="fp-link" onClick={this.details}>
                Contact {affiliate}
              </button>
              <button type="button" className="fp-link" onClick={this.cancel}>
                Close
              </button>
            </div>
          </div>
        </Modal>

        <Modal
          show={this.state.detailModal}
          onHide={this.cancel}
          centered
          dialogClassName="fp-modal"
        >
          <div className="fp-modal__body">
            <h2 className="fp-modal__title">{affiliate}</h2>
            <p className="fp-modal__text">
              Reach out if you have questions about joining Karamale.
            </p>
            <div className="fp-modal__contact">
              {affiliate_email && <a href={`mailto:${affiliate_email}`}>{affiliate_email}</a>}
              {affiliate_phone && <a href={`tel:${affiliate_phone}`}>{affiliate_phone}</a>}
            </div>
            <div className="fp-modal__actions">
              <button type="button" className="fp-btn fp-btn--brand" onClick={this.back}>
                Back
              </button>
            </div>
            <div className="fp-modal__foot">
              <button type="button" className="fp-link" onClick={this.cancel}>
                Close
              </button>
            </div>
          </div>
        </Modal>
      </div>
    )
  }
}

export default Home
